import { PropertyType } from '@/api/enumerations';
import { FactorsSections } from '@/api/manualCalculation/types';
import { AggregatedFactors } from '@/api/workOrders/types';
import {
  independentStructuresGroup,
  parcelGroup,
} from '@/constants/selectOptions';

export const Constants = {
  addVariable: 'Adicionar variável',
  addVariableTooltip:
    'Defina os critérios de comparação entre os elementos adicionados. Adicione até 8 variáveis.',
  systemVariables: 'Variáveis do sistema',
  createVariable: 'Criar variável',
  newVariable: 'Nova variável',
  homogenizationCaption: 'HOMOGENEIZAÇÃO',
  factors: 'Fatores',
  factorsCaption: 'FATORES',
  selectOption: 'selecione uma opção',
  trueOption: 'verdadeiro',
  falseOption: 'falso',
  item: 'Item',
  totalPrice: 'Preço Total (R$)',
  unitValue: 'Valor Unitário (R$/m²)',
  offer: 'Oferta',
  offerFactor: 'Fator / Oferta',
  homogenizedValue: 'Valor Homogeneizado (R$/m²)',
  sanitizedValue: 'Valor Saneado',
  adjustmentInterval: 'Intervalo de Ajuste',
  fieldPlaceholder: 'campo',
  deleteColumn: 'deletar coluna',
  removeVariableTitle: 'Remover variável?',
  removeVariableTextPrefix: 'Os dados inseridos em',
  removeVariableTextSuffix: 'serão perdidos permanentemente.',
  cancel: 'Cancelar',
  removeVariable: 'Remover variável',
  homogenizationTable: 'tabela-homogeneizacao',
  factorsTable: 'tabela-dados-fatores',
  aggregatedFactorsTable: 'tabela-dados-fatores-agregados',
};

export interface SystemVariable {
  label: string;
  inputDataField: string;
}

export const getSystemVariables = (
  propertyType: PropertyType
): SystemVariable[] => {
  if (independentStructuresGroup.has(propertyType)) {
    return [
      { label: 'Área Construída (m²)', inputDataField: 'area' },
      { label: 'Área do Terreno (m²)', inputDataField: 'land_area' },
      { label: 'Padrão Construtivo', inputDataField: 'constructive_pattern' },
      { label: 'Idade Estimada', inputDataField: 'age' },
      { label: 'Estado de Conservação', inputDataField: 'preservation_status' },
      { label: 'Renda IBGE (2022)', inputDataField: 'ibge_income' },
      { label: 'Vagas', inputDataField: 'parking_spaces' },
      { label: 'Quartos', inputDataField: 'rooms' },
    ];
  }
  if (parcelGroup.has(propertyType)) {
    return [
      { label: 'Área do Terreno (m²)', inputDataField: 'land_area' },
      { label: 'Renda IBGE (2022)', inputDataField: 'ibge_income' },
    ];
  }
  return [
    { label: 'Área Privativa (m²)', inputDataField: 'area' },
    { label: 'Padrão Construtivo', inputDataField: 'constructive_pattern' },
    { label: 'Idade Estimada', inputDataField: 'age' },
    { label: 'Estado de Conservação', inputDataField: 'preservation_status' },
    { label: 'Renda IBGE (2022)', inputDataField: 'ibge_income' },
    { label: 'Vagas', inputDataField: 'parking_spaces' },
    { label: 'Quartos', inputDataField: 'rooms' },
  ];
};

export const presentAggFactors = [
  {
    label: '1',
    value: 'Média',
  },
  {
    label: '2',
    value: 'Desvio Padrão',
  },
  {
    label: '3',
    value: 'Coeficiente de Variação',
  },
  {
    label: '4',
    value: 'Validação de Coeficiente',
  },
  {
    label: '5',
    value: 'Coeficiente Utilizado',
  },
];

export const selectOfferOptions = [
  { value: 'oferta', label: 'oferta' },
  { value: 'transacionado', label: 'transacionado' },
];

export const RESULT_FIELD_LABELS = {
  sanitized_mean: 'Valor Unitário Médio',
  lower_limit: 'Valor Unitário Mínimo',
  upper_limit: 'Valor Unitário Máximo',
  market_value: 'Valor Total Médio',
  lower_limit_total: 'Valor Total Mínimo',
  upper_limit_total: 'Valor Total Máximo',
  lower_limit_confidence: 'Amplitude Mínima do Modelo',
  upper_limit_confidence: 'Amplitude Máximo do Modelo',
  market_value_rounded_1pct_plus: 'Valor de Mercado com Arredondamento de +1%',
  market_value_rounded_1pct_minus: 'Valor de Mercado com Arredondamento de -1%',
  rounded_market_value: 'Valor de Mercado Arredondado',
} as const;

export const STATISTIC_FIELD_LABELS = {
  homogenized_elements: 'Elementos Homogeneizados',
  homogenized_mean: 'Média Homogeneizada',
  homogenized_lower_mean: 'Limite Inferior (-30%)',
  homogenized_upper_mean: 'Limite Superior (+30%)',
  sanitized_elements: 'Elementos Saneados',
  sanitized_mean: 'Média Saneada',
  sanitized_std: 'Desvio Padrão',
  sanitized_lower_mean: 'Limite Inferior (-30%)',
  sanitized_upper_mean: 'Limite Superior (+30%)',
  t_student: 'T de Student',
  upper_limit: 'Limite Máximo',
  lower_limit: 'Limite Mínimo',
  confidence_interval: 'Intervalo de Confiabilidade (%)',
  precision_level: 'Grau de Precisão',
} as const;

export const FAC_GRAPHS_FIELD_LABELS = {
  unit_land_prediction_power:
    'Poder de predição do modelo - valores unitários dos terrenos',
} as const;

export const FACTOR_TABS: Array<{ key: FactorsSections; label: string }> = [
  { key: 'homogenization', label: 'Homogeneização' },
  { key: 'variation_and_sanitation', label: 'Variação e Saneamento' },
  { key: 'foundation', label: 'Fundamentação' },
  { key: 'result', label: 'Resultado' },
];

export const getAggCellValue = (
  rowIndex: number,
  factor: AggregatedFactors
): number => {
  switch (rowIndex) {
    case 0:
      return factor.mean_ppsm;
    case 1:
      return factor.std_ppsm;
    case 2:
      return factor.coeff;
    case 3:
      return factor.coeff_validation ? 1 : 0;
    case 4:
      return factor.coeff_utilized ? 1 : 0;
    default:
      return 0;
  }
};
